/**
 * Safe clipboard write utility
 *
 * Handles the NotAllowedError that occurs when the document is not focused
 * (e.g., inside modals, after tab switches, or when focus is in a video stream).
 * Falls back to the legacy document.execCommand('copy') approach.
 *
 * @param text - The text to copy to the clipboard
 * @returns true if the copy succeeded, false otherwise
 *
 * @example
 * const ok = await safeWriteClipboard(someUrl);
 * if (ok) toast.success('Copied!');
 * else toast.error('Could not copy — try manually');
 */
export async function safeWriteClipboard(text: string): Promise<boolean> {
  // Prefer the modern Clipboard API when available and document is focused
  if (navigator.clipboard?.writeText && document.hasFocus()) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to legacy fallback
    }
  }

  // Legacy fallback — works even when the document isn't focused
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // Position off-screen to avoid visual flicker
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
